/**
 * One-Time Sharing Tests — 4 items, each completed once.
 *
 * Available from day 3 onward. Section persists until all four are
 * verified_ok, then hides.
 *
 * For Yes ("looks correct"): checkbox-toggled → verified_ok.
 * For No ("doesn't render correctly"): testers tap a small "Report issue"
 * button under each item → writes issue_found and routes to Asana.
 */

import { useState } from "react";
import { format } from "date-fns";
import type { Tester, Verification, VerificationStatus } from "../../types";
import { Section, CheckRow } from "../Section";
import { CheckboxRow } from "../VerificationButtons";
import { SHARING_KEYS } from "../../lib/conditionKeys";
import { openAsanaBugForm } from "../../lib/asana";

interface SharingTest {
  key: string;
  label: string;
  sub: string;
}

// Keys must stay in sync with SHARING_KEYS in lib/conditionKeys.ts, which is
// what engagement and the Coverage Matrix read. The two DM tests were removed
// on 2026-08-17: direct-message sharing is not in this release. See the note
// on SHARING_KEYS for how to restore them.
const TESTS: SharingTest[] = [
  {
    key: "sharing_workout_feed",
    label: "Shared a workout to the public feed",
    sub: "Confirm it displays correctly.",
  },
  {
    key: "sharing_map_feed",
    label: "Shared a map (route) to the public feed",
    sub: "Confirm the route displays correctly.",
  },
];

interface SharingTestsSectionProps {
  profile: Tester;
  currentDayNum: number;
  today: Date;
  asanaUrl: string;
  getStatus: (key: string, day: number) => Verification | null;
  setStatus: (input: {
    conditionKey: string;
    testingDay: number;
    status: VerificationStatus;
  }) => Promise<void>;
}

export function SharingTestsSection({
  profile,
  currentDayNum,
  today,
  asanaUrl,
  getStatus,
  setStatus,
}: SharingTestsSectionProps) {
  // Available from day 3 onward.
  if (currentDayNum < 3) return null;

  // testing_day = 1 by convention for one-time tests.
  const allComplete = TESTS.every(
    (t) => getStatus(t.key, 1)?.status === "verified_ok",
  );
  if (allComplete) return null;

  const todayDate = format(today, "EEE, MMM d");

  const handleConfirm = (test: SharingTest) => () =>
    setStatus({ conditionKey: test.key, testingDay: 1, status: "verified_ok" });

  const handleReportIssue = async (test: SharingTest) => {
    await setStatus({ conditionKey: test.key, testingDay: 1, status: "issue_found" });
    const dayLabel = `Day ${currentDayNum} of ${
      profile.tester_type === "Volunteer" ? "14" : "7"
    }`;
    openAsanaBugForm(asanaUrl, {
      email: profile.email,
      wordpressUserId: profile.wordpress_user_id,
      dayLabel,
      dayDate: todayDate,
      conditionLabel: `Sharing test: ${test.label}`,
    });
  };

  // Verified for sanity logging only — don't surface this widely.
  // Sentinel useState so React knows we re-render on confirm clicks even if
  // status comes from a stale cache snapshot.
  const [, setTick] = useState(0);

  return (
    <Section title="One-time sharing tests" meta="Day 3+">
      {TESTS.map((test) => {
        const row = getStatus(test.key, 1);
        const status = row?.status ?? null;
        return (
          <div key={test.key}>
            <CheckRow
              label={test.label}
              sub={test.sub}
              flagged={status === "issue_found"}
              trailing={
                <CheckboxRow
                  status={status}
                  onCheck={async () => {
                    await handleConfirm(test)();
                    setTick((t) => t + 1);
                  }}
                />
              }
            />
            {status !== "verified_ok" && (
              <div className="px-3 pb-2 -mt-1 flex justify-end">
                <button
                  type="button"
                  onClick={async () => {
                    await handleReportIssue(test);
                    setTick((t) => t + 1);
                  }}
                  className="text-[10px] text-rose-700 hover:text-rose-900 underline"
                >
                  Report an issue with this share →
                </button>
              </div>
            )}
          </div>
        );
      })}
    </Section>
  );
}

// Re-export for callers; keeps SHARING_KEYS imported via the conditionKeys module.
export { SHARING_KEYS };
